import React from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Check, X, Tag, Sparkles } from 'lucide-react';

const TONES = [
  { tone: 'jackstone-blue', name: 'Jackstone Blue', use: 'Primary · interactive', hex: '#39678F' },
  { tone: 'navy', name: 'Navy', use: 'Strong neutral · approved phrases', hex: '#33424F' },
  { tone: 'blue', name: 'Bright Blue', use: 'Interactive accents', hex: '#1997BD' },
  { tone: 'teal', name: 'Teal', use: 'Fresh · secondary timeline', hex: '#50C9B3' },
  { tone: 'green', name: 'Green', use: 'Do · use · success', hex: '#4CD964' },
  { tone: 'purple', name: 'Purple', use: 'Premium · strategic', hex: '#4B4B9D' },
  { tone: 'grey', name: 'Cool Grey', use: 'Neutral category labels', hex: '#7B8D9E' },
  { tone: 'red', name: 'Red', use: "Don't · avoid · alert", hex: '#B30035' },
];

export default function BrandUIKit() {
  return (
    <div className="space-y-8" data-testid="uikit-section">
      <div>
        <h1 className="text-4xl md:text-5xl font-display font-normal text-jackstone-blue mb-4">
          Brand UI Kit
        </h1>
        <p className="text-base text-body leading-relaxed">
          A single board of every approved badge tone and button state. Each pairing is
          contrast-checked, so designers and developers can copy the exact look at a glance —
          no guesswork, no off-brand colours.
        </p>
      </div>

      {/* Badge matrix */}
      <Card className="p-8">
        <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-2">
          Badges
        </h2>
        <p className="text-sm text-subtle mb-6">
          Every tone is available in <strong>Soft</strong> and <strong>Solid</strong> fills, with
          <strong> rounded</strong> or <strong>pill</strong> corners.
        </p>

        <div className="hidden sm:grid grid-cols-[150px_1fr] gap-4 pb-2 mb-2 border-b border-border">
          <p className="text-xs uppercase tracking-wide text-subtle">Tone</p>
          <p className="text-xs uppercase tracking-wide text-subtle">Soft · Solid · Soft pill · Solid pill</p>
        </div>

        <div className="space-y-0">
          {TONES.map((t) => (
            <div
              key={t.tone}
              className="grid sm:grid-cols-[150px_1fr] items-center gap-2 sm:gap-4 py-3 border-b border-border last:border-0"
              data-testid={`uikit-badge-row-${t.tone}`}
            >
              <div>
                <p className="text-sm font-semibold text-body">{t.name}</p>
                <p className="text-xs text-subtle">{t.use}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={t.tone} fill="soft">Soft</Badge>
                <Badge tone={t.tone} fill="solid">Solid</Badge>
                <Badge tone={t.tone} fill="soft" shape="pill">Soft pill</Badge>
                <Badge tone={t.tone} fill="solid" shape="pill">Solid pill</Badge>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 rounded-lg bg-surface border border-border">
          <p className="text-xs text-subtle mb-1">Copy syntax</p>
          <code className="text-sm text-navy font-mono">
            {'<Badge tone="green" fill="soft" shape="pill" />'}
          </code>
        </div>
      </Card>

      {/* Semantic usage */}
      <div>
        <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
          Semantic Usage
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <Check className="h-5 w-5 text-success" strokeWidth={2.5} />
              <h3 className="text-lg font-heading font-semibold text-body">Green = Do / Use</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge tone="green" fill="soft">Precision</Badge>
              <Badge tone="green" fill="soft">Reliable</Badge>
              <Badge tone="green" fill="soft">Proven</Badge>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <X className="h-5 w-5 text-destructive" strokeWidth={2.5} />
              <h3 className="text-lg font-heading font-semibold text-body">Red = Don't / Avoid</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge tone="red" fill="soft">Revolutionary</Badge>
              <Badge tone="red" fill="soft">Game-changing</Badge>
              <Badge tone="red" fill="soft">Trendy</Badge>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="h-5 w-5 text-jackstone-blue" strokeWidth={2.5} />
              <h3 className="text-lg font-heading font-semibold text-body">Cool Grey = Category</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge tone="grey" fill="soft">Heritage</Badge>
              <Badge tone="grey" fill="soft">Engineering</Badge>
              <Badge tone="grey" fill="soft">Innovation</Badge>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-5 w-5 text-jackstone-blue" strokeWidth={2.5} />
              <h3 className="text-lg font-heading font-semibold text-body">Blue Solid = Eyebrow</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge tone="jackstone-blue" fill="solid">Core Voice</Badge>
              <Badge tone="jackstone-blue" fill="solid">Booth Design</Badge>
            </div>
          </Card>
        </div>
      </div>

      {/* Buttons */}
      <Card className="p-8">
        <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-2">
          Buttons
        </h2>
        <p className="text-sm text-subtle mb-6">
          Primary actions use solid Jackstone Blue. Secondary actions use the blue outline.
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <Button data-testid="uikit-btn-primary">Primary</Button>
          <Button variant="outline" data-testid="uikit-btn-outline">Outline</Button>
          <Button variant="secondary" data-testid="uikit-btn-secondary">Secondary</Button>
          <Button variant="ghost" data-testid="uikit-btn-ghost">Ghost</Button>
          <Button variant="destructive" data-testid="uikit-btn-destructive">Destructive</Button>
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-5 pt-5 border-t border-border">
          <Button size="sm">Small</Button>
          <Button>Default</Button>
          <Button size="lg">Large</Button>
        </div>
      </Card>
    </div>
  );
}
